import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { PWAService } from './pwa.service';
import { NotificationService } from './notification.service';

export interface DeepLinkData {
  type: 'dashboard' | 'product' | 'sale' | 'report' | 'widget';
  id?: string;
  data?: any;
  title?: string;
  description?: string;
}

export interface ShareableLink {
  url: string;
  title: string;
  description: string;
  image?: string;
}

/**
 * Deep Link Service - Disruptive feature for sharing and navigation
 * Allows users to create and share deep links to specific content
 */
@Injectable({
  providedIn: 'root',
})
export class DeepLinkService {
  private router = inject(Router);
  private pwaService = inject(PWAService);
  private notificationService = inject(NotificationService);

  private readonly baseUrl = window.location.origin;

  /**
   * Generate a shareable deep link
   */
  generateDeepLink(data: DeepLinkData): ShareableLink {
    const linkId = this.generateLinkId();
    const encodedData = btoa(JSON.stringify(data));

    // Store link data for quick access
    localStorage.setItem(`deeplink_${linkId}`, encodedData);

    const url = `${this.baseUrl}/dl/${linkId}`;

    return {
      url,
      title: data.title || this.getDefaultTitle(data.type),
      description: data.description || this.getDefaultDescription(data.type),
      image: this.getDefaultImage(data.type),
    };
  }

  /**
   * Parse and navigate to deep link
   */
  async navigateFromDeepLink(linkId: string): Promise<boolean> {
    try {
      const storedData = localStorage.getItem(`deeplink_${linkId}`);
      if (!storedData) {
        console.warn('Deep link not found:', linkId);
        return false;
      }

      const data: DeepLinkData = JSON.parse(atob(storedData));
      return await this.navigateToContent(data);
    } catch (error) {
      console.error('Error processing deep link:', error);
      return false;
    }
  }

  /**
   * Share link using Web Share API or fallback
   */
  async shareLink(shareableLink: ShareableLink): Promise<boolean> {
    try {
      if (this.pwaService.canShare()) {
        await navigator.share({
          title: shareableLink.title,
          text: shareableLink.description,
          url: shareableLink.url,
        });
        return true;
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareableLink.url);
        this.notificationService.showNotification({
          title: 'Link copiado!',
          body: 'O link foi copiado para a área de transferência',
          icon: 'link',
        });
        return true;
      }
    } catch (error) {
      console.error('Error sharing link:', error);
      return false;
    }
  }

  /**
   * Create quick share for current page
   */
  shareCurrentPage(): ShareableLink {
    const currentRoute = this.router.url;
    let type: DeepLinkData['type'] = 'dashboard';
    let title = 'Estokai - Gestão Inteligente';
    let description = 'Confira este conteúdo no Estokai';

    // Detect current page type
    if (currentRoute.includes('/products')) {
      type = 'product';
      title = 'Produtos - Estokai';
      description = 'Confira os produtos no Estokai';
    } else if (currentRoute.includes('/sales')) {
      type = 'sale';
      title = 'Vendas - Estokai';
      description = 'Acompanhe as vendas no Estokai';
    } else if (currentRoute.includes('/reports')) {
      type = 'report';
      title = 'Relatórios - Estokai';
      description = 'Veja os relatórios no Estokai';
    }

    return this.generateDeepLink({
      type,
      title,
      description,
      data: { route: currentRoute },
    });
  }

  /**
   * Create widget share link
   */
  shareWidget(widgetId: string, widgetData: any): ShareableLink {
    return this.generateDeepLink({
      type: 'widget',
      id: widgetId,
      title: `Widget: ${widgetData.title || 'Personalizado'}`,
      description: 'Confira este widget personalizado no Estokai',
      data: widgetData,
    });
  }

  private generateLinkId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }

  private getDefaultTitle(type: DeepLinkData['type']): string {
    const titles = {
      dashboard: 'Dashboard - Estokai',
      product: 'Produto - Estokai',
      sale: 'Venda - Estokai',
      report: 'Relatório - Estokai',
      widget: 'Widget Personalizado - Estokai',
    };
    return titles[type];
  }

  private getDefaultDescription(type: DeepLinkData['type']): string {
    const descriptions = {
      dashboard: 'Acompanhe seus indicadores em tempo real',
      product: 'Detalhes do produto e informações de estoque',
      sale: 'Informações detalhadas da venda',
      report: 'Relatório analítico e insights do negócio',
      widget: 'Widget personalizado para seu dashboard',
    };
    return descriptions[type];
  }

  private getDefaultImage(type: DeepLinkData['type']): string {
    // Return appropriate default images for each type
    return `${this.baseUrl}/assets/icons/estokai-share-${type}.png`;
  }

  private async navigateToContent(data: DeepLinkData): Promise<boolean> {
    try {
      switch (data.type) {
        case 'dashboard':
          await this.router.navigate(['/dashboard']);
          break;
        case 'product':
          if (data.id) {
            await this.router.navigate(['/products', data.id]);
          } else {
            await this.router.navigate(['/products']);
          }
          break;
        case 'sale':
          if (data.id) {
            await this.router.navigate(['/sales', data.id]);
          } else {
            await this.router.navigate(['/sales']);
          }
          break;
        case 'report':
          if (data.data?.route) {
            await this.router.navigate([data.data.route]);
          } else {
            await this.router.navigate(['/reports']);
          }
          break;
        case 'widget':
          // Navigate to dashboard and restore widget
          await this.router.navigate(['/dashboard']);
          if (data.data) {
            // Trigger widget restoration
            this.restoreSharedWidget(data.data);
          }
          break;
        default:
          await this.router.navigate(['/dashboard']);
      }
      return true;
    } catch (error) {
      console.error('Navigation error:', error);
      return false;
    }
  }

  private restoreSharedWidget(widgetData: any): void {
    // Emit event or call service to restore shared widget
    const event = new CustomEvent('restore-shared-widget', {
      detail: widgetData,
    });
    window.dispatchEvent(event);
  }
}
